import React from 'react';
import { Link } from 'react-router-dom';
import './Home.css';
import HeroImage from '../assets/img/header-img-cheesecake.webp';
import TakeawayImage from '../assets/img/takeaway.webp';
import LunchOfferImage from '../assets/img/lunch.webp';
import BrunchImage from '../assets/img/brunch2.webp';
import KafiPassImage from '../assets/img/kafi-pass.webp';
import CoffeeCakeSandwichesImage from '../assets/img/coffee_cake_and_sandwiches_2.webp';
import WholeCheesecakesImage from '../assets/img/whole_cheesecakes.webp';
import VouchersImage from '../assets/img/vouchers.webp';
import HaekelTreffImage from '../assets/img/haekel-treff-winter.webp';
import KuerbisSchnitzenImage from '../assets/img/event-kuerbis-schnitzen.webp';
import { getSpecialOpeningHoursForDate, getZurichDateKey, siteInfo } from '../data/siteInfo';
import { formatLunchDate, getFeaturedLunchOffer } from '../data/lunchOffers';
import { Seo, buildBreadcrumbSchema, buildCafeSchema } from '../components/Seo';

const phoneIcon = (
  <svg viewBox='0 0 24 24' role='img' aria-hidden='true'>
    <path
      d='M21 16.9v2.95a1.95 1.95 0 0 1-2.13 1.95A19.3 19.3 0 0 1 10.44 18a19 19 0 0 1-5.9-5.9 19.3 19.3 0 0 1-3.8-8.48A1.95 1.95 0 0 1 2.68 1.5H5.6A1.95 1.95 0 0 1 7.55 3.2c.12 1.02.36 2.03.7 2.99a1.95 1.95 0 0 1-.44 2.06L6.57 9.5a15.6 15.6 0 0 0 7.93 7.93l1.25-1.24a1.95 1.95 0 0 1 2.06-.45c.96.35 1.97.59 2.99.71A1.95 1.95 0 0 1 21 16.9z'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.7'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const emailIcon = (
  <svg viewBox='0 0 24 24' role='img' aria-hidden='true'>
    <path
      d='M3 6.75A1.75 1.75 0 0 1 4.75 5h14.5A1.75 1.75 0 0 1 21 6.75v10.5A1.75 1.75 0 0 1 19.25 19H4.75A1.75 1.75 0 0 1 3 17.25V6.75z'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.7'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M3.4 7.3 12 13.2l8.6-5.9'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.7'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const instagramIcon = (
  <svg viewBox='0 0 24 24' role='img' aria-hidden='true'>
    <rect
      x='3.5'
      y='3.5'
      width='17'
      height='17'
      rx='4.5'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.7'
    />
    <circle
      cx='12'
      cy='12'
      r='4'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.7'
    />
    <circle cx='17.2' cy='6.8' r='1.1' fill='currentColor' />
  </svg>
);

const clockIcon = (
  <svg viewBox='0 0 24 24' role='img' aria-hidden='true'>
    <circle
      cx='12'
      cy='12'
      r='8.5'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.7'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M12 7.8v4.6l3 1.8'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.7'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const locationIcon = (
  <svg viewBox='0 0 24 24' role='img' aria-hidden='true'>
    <path
      d='M12 21s-5.6-5.1-5.6-10.1a5.6 5.6 0 1 1 11.2 0C17.6 15.9 12 21 12 21z'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.7'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <circle
      cx='12'
      cy='10.8'
      r='1.9'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.7'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const offerings = [
  {
    id: 'coffee-cake-sandwiches',
    title: 'Kaffee, Kuchen und Sandwiches',
    label: 'Ganztags beliebt',
    detail: 'Feine Kaffeespezialit\u00e4ten, hausgemachte Cheesecakes und herzhafte Sandwiches f\u00fcr zwischendurch.',
    image: CoffeeCakeSandwichesImage
  },
  {
    id: 'brunch',
    title: 'Brunch',
    label: 'Samstag & Sonntag',
    detail: 'Durchgehender Wochenend-Brunch mit regionalen Zutaten und viel Hausgemachtem.',
    points: [
      'Mit K\u00e4se, Aufschnitt, Antipasti, Zopf, Br\u00f6tli, Gipfeli und Bircherm\u00fcesli.',
      'Inklusive Heissgetr\u00e4nk sowie Zimtschnecke oder Muffin.',
      'Auch vegan oder vegetarisch erh\u00e4ltlich.'
    ],
    image: BrunchImage
  },
  {
    id: 'takeaway',
    title: 'Take-away',
    label: 'Schnell mitnehmen',
    detail: 'Frisch zubereitete Speisen und Getr\u00e4nke f\u00fcr unterwegs, ohne auf Qualit\u00e4t zu verzichten.',
    image: TakeawayImage
  },
  {
    id: 'lunch',
    title: 'Mittagessen',
    label: 'Tagesgerichte & Speisekarte',
    detail: 'Wechselnde Tagesgerichte von Mittwoch bis Freitag, dazu weitere Mittagsoptionen aus der Speisekarte.',
    image: LunchOfferImage
  },
  {
    id: 'kafi-pass',
    title: 'Kafi Pass',
    label: 'Treuekarte',
    detail: 'Mit dem Kafi Pass ist der n\u00e4chste Kaffee gratis, nachdem Sie zehn Kaffees gekauft haben.',
    image: KafiPassImage
  },
  {
    id: 'vouchers',
    title: 'Geschenkgutscheine',
    label: 'Direkt an der Kasse',
    detail: 'Verschenken Sie eine genussvolle Zeit bei Sarita\'s. Den Wert des Geschenkgutscheins bestimmen Sie selbst. Unsere Geschenkgutscheine sind direkt bei uns an der Kasse erh\u00e4ltlich.',
    image: VouchersImage
  },
  {
    id: 'whole-cheesecakes',
    title: 'Ganze Cheesecakes',
    label: 'Auf Vorbestellung',
    detail: 'Ganze Cheesecakes sind auf Bestellung erh\u00e4ltlich. Bitte mindestens 24 Stunden im Voraus bestellen.',
    points: [
      'Erh\u00e4ltlich in 20 cm oder 26 cm Durchmesser.',
      'M\u00f6gliche Sorten: Nutella, Zitrone, diverse Beeren, Snickers, Schocki/Vanille (Marmor), Kokos etc.',
      'Preis je nach Gr\u00f6sse und Sorte zwischen 55 und 70 CHF.'
    ],
    image: WholeCheesecakesImage
  }
];

const events = [
  {
    id: 'kuerbis-schnitzen',
    title: 'Kürbis schnitzen',
    label: 'Kreativer Herbstnachmittag',
    date: 'Mittwoch, 28. Oktober 2026',
    dateIso: '2026-10-28',
    time: '14:00 - 16:30 Uhr',
    detail: 'Schnitz deinen Kürbis im Sarita\'s! Für Kinder in Begleitung eines Erwachsenen und erwachsene Kinder. CHF 25.- inklusive Kürbis, Hauseistee und hausgemachten Brownies. Schnitzwerkzeug und Arbeitsfläche stehen bereit.',
    signup: 'Anmeldung bis Montag, 26. Oktober per Mail oder Telefon.',
    image: KuerbisSchnitzenImage,
    imageClassName: 'home-event-card__image--poster',
    path: '/kuerbis-schnitzen'
  },
  {
    id: 'haekel-treff',
    title: 'Kaffee, Kuchen und Häkeln',
    label: 'Weihnachtlicher Häkel-Treff',
    date: 'Samstag, 21. November 2026',
    dateIso: '2026-11-21',
    time: '11:00 - 16:30 Uhr',
    detail: 'Bring deine eigene Häkel- oder Strickarbeit mit. Für Anfänger:innen stehen Weihnachts-Startersets inklusive Material für CHF 15.- bis 25.- zur Auswahl. Sarah hilft bei Fragen und bietet Anleitungen für verschiedene Weihnachtssujets.',
    signup: 'Anmeldung bis Donnerstag, 19. November per Mail oder Telefon.',
    image: HaekelTreffImage,
    path: '/haekel-treff'
  }
];

const quickFacts = [
  {
    label: 'Öffnungszeiten',
    value: (
      <>
        {siteInfo.openingHours.map((entry) => (
          <span key={entry.days} className='home-fact-line'>
            <span className='home-fact-icon'>{clockIcon}</span>
            <span className='home-fact-text'>
              {entry.days}: {entry.hours}
            </span>
          </span>
        ))}
      </>
    )
  },
  {
    label: 'Standort',
    value: (
      <span className='home-fact-line'>
        <span className='home-fact-icon home-fact-icon--location'>{locationIcon}</span>
        <span className='home-fact-text'>{siteInfo.addressLine1}, {siteInfo.addressLine2}</span>
      </span>
    )
  },
  {
    label: 'Kontakt',
    value: (
      <>
        <span className='home-fact-line'>
          <span className='home-fact-icon'>{phoneIcon}</span>
          <a href={siteInfo.phoneHref} className='home-fact-text'>{siteInfo.phone}</a>
        </span>
        <span className='home-fact-line'>
          <span className='home-fact-icon'>{emailIcon}</span>
          <a href={`mailto:${siteInfo.email}`} className='home-fact-text'>{siteInfo.email}</a>
        </span>
        <span className='home-fact-line'>
          <span className='home-fact-icon'>{instagramIcon}</span>
          <a href={siteInfo.instagramUrl} target='_blank' rel='noreferrer' className='home-fact-text'>{siteInfo.instagramHandle}</a>
        </span>
      </>
    )
  }
];

function Home() {
  const featuredLunch = getFeaturedLunchOffer();
  const todaysSpecialHours = getSpecialOpeningHoursForDate();
  const today = getZurichDateKey();
  const upcomingEvents = events.filter((event) => event.dateIso >= today);

  return (
    <div className='home-page'>
      <Seo
        title='Cafe & Bistro in Hinwil'
        description="Sarita's ist ein Cafe und Bistro am Bahnhofplatz 1 in Hinwil mit Kaffee, hausgemachten Cheesecakes, Brunch, Lunch und Take-away."
        path='/'
        schema={[
          {
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: siteInfo.name,
            url: siteInfo.siteUrl,
            inLanguage: 'de-CH'
          },
          buildCafeSchema(),
          buildBreadcrumbSchema([{ name: 'Startseite', path: '/' }])
        ]}
      />
      <section
        className='home-hero'
        style={{
          backgroundImage: `url(${HeroImage})`
        }}
      >
        <div className='home-hero__content container'>
          {todaysSpecialHours ? (
            <aside className='home-special-hours' role='status'>
              <strong>{todaysSpecialHours.status ? 'Heute geschlossen' : 'Heutige Öffnungszeiten'}</strong>
              <span>
                Am {todaysSpecialHours.displayDate}
                {todaysSpecialHours.name ? ` (${todaysSpecialHours.name})` : ''}
                {todaysSpecialHours.status
                  ? ` bleibt Sarita's geschlossen.`
                  : ` ist Sarita's von ${todaysSpecialHours.hours} Uhr geöffnet.`}
              </span>
              <Link to='/contact'>Alle speziellen Öffnungszeiten</Link>
            </aside>
          ) : null}
          <h1 className='home-hero__statement'>
            Genuss zum Verweilen. <br/> Willkommen bei Sarita&apos;s.
          </h1>
          <div className='home-hero__actions'>
            <Link to='/menu' className='home-btn home-btn--primary'>Speisekarte entdecken</Link>
          </div>
          <ul className='home-hero__facts' aria-label='Café-Kurzinfos'>
            {quickFacts.map((fact) => (
              <li key={fact.label}>
                <span>{fact.label}</span>
                <strong>{fact.value}</strong>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className='home-section'>
        <div className='container'>
          <header className='home-section__header'>
            <p className='section-eyebrow'>Angebote</p>
            <h2 className='section-title'>Unsere Angebote auf einen Blick</h2>
          </header>
          <div className='home-moments-grid'>
            {offerings.map((item) => (
              <article key={item.id} className='home-moment-card home-moment-card--offer panel-surface reveal'>
                <img src={item.image} alt={item.title} className='home-moment-card__image' />
                <div className='home-moment-card__body'>
                  <p className='home-moment-card__time'>{item.label}</p>
                  <h3>{item.title}</h3>
                  <p>{item.detail}</p>
                  {item.points ? (
                    <ul className='home-moment-card__highlights'>
                      {item.points.map((point) => (
                        <li key={point}>{point}</li>
                      ))}
                    </ul>
                  ) : null}
                  {item.id === 'lunch' && featuredLunch.offer ? (
                    <div className='home-moment-card__lunch'>
                      <p className='home-moment-card__lunch-label'>{featuredLunch.label}</p>
                      <h4>{featuredLunch.offer.title}</h4>
                      <p className='home-moment-card__lunch-date'>
                        {formatLunchDate(featuredLunch.offer.date)}
                      </p>
                      {featuredLunch.offer.price ? (
                        <p className='home-moment-card__lunch-price'>{featuredLunch.offer.price}</p>
                      ) : null}
                      {featuredLunch.offer.detail ? (
                        <p className='home-moment-card__lunch-detail'>{featuredLunch.offer.detail}</p>
                      ) : null}
                      <Link to='/lunch' className='home-btn home-btn--primary home-moment-card__link'>Mittagessen ansehen</Link>
                    </div>
                  ) : null}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className='home-section home-events-section'>
        <div className='container'>
          <header className='home-section__header'>
            <p className='section-eyebrow'>Events</p>
            <h2 className='section-title'>Aktuelle Events im Sarita&apos;s</h2>
          </header>
          {upcomingEvents.length > 0 ? (
            <div className='home-events-grid'>
              {upcomingEvents.map((event) => (
                <article key={event.id} className='home-event-card panel-surface reveal'>
                  <img src={event.image} alt={event.title} className={`home-event-card__image ${event.imageClassName || ''}`} />
                  <div className='home-event-card__body'>
                    <p className='home-event-card__label'>{event.label}</p>
                    <h3>{event.title}</h3>
                    <div className='home-event-card__meta'>
                      <span>{event.date}</span>
                      <span>{event.time}</span>
                    </div>
                    <p>{event.detail}</p>
                    <p className='home-event-card__signup'>{event.signup}</p>
                    <Link to={event.path} className='home-btn home-btn--primary home-event-card__link'>
                      Mehr zum Event
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <p className='home-events-empty'>Derzeit sind keine Events geplant. Neue Termine geben wir hier bekannt.</p>
          )}
        </div>
      </section>
    </div>
  );
}

export default Home;
